import {useQueryClient} from '@tanstack/react-query';
import {useEffect} from 'react';
import {Navigate, Outlet, useLocation} from 'react-router';
import {avisosDeSesion, EVENTO_SESION_PERDIDA} from '../api/cliente';
import {CLAVE_SESION, useSesionActual} from '../api/sesion';
import {useIdioma} from '../i18n/contexto-idioma';
import {tr} from '../i18n/idioma';
import {ContextoDeSesion} from './sesion-contexto';

// Envuelve toda la aplicacion: sin sesion lleva a ingresar; con la sesion a
// medias (codigo de dos pasos, contrasena por cambiar, dos pasos por
// configurar) lleva al paso que falta. Cuando el servidor avisa que la sesion
// se perdio, vuelve a consultar y actua igual. Con la sesion abierta aplica
// el idioma guardado en el perfil de la persona.
export function RequiereSesion() {
  const sesion = useSesionActual();
  const cliente = useQueryClient();
  const ubicacion = useLocation();
  const {idioma, cambiar} = useIdioma();
  const preferido = sesion.data?.usuario.preferencias.idioma;

  useEffect(() => {
    const perdida = () => void cliente.invalidateQueries({queryKey: CLAVE_SESION});
    avisosDeSesion.addEventListener(EVENTO_SESION_PERDIDA, perdida);
    return () => avisosDeSesion.removeEventListener(EVENTO_SESION_PERDIDA, perdida);
  }, [cliente]);

  useEffect(() => {
    if (preferido && preferido !== idioma) cambiar(preferido);
  }, [preferido, idioma, cambiar]);

  if (sesion.isPending) {
    return <p className="p-8 text-sm text-apagado">{tr('Comprobando la sesión…', 'Checking your session…')}</p>;
  }
  if (sesion.isError) {
    return (
      <div className="m-8 rounded-lg border border-alerta/30 bg-red-50 p-4 text-sm">
        <div className="font-medium text-alerta">
          {tr('No se pudo comprobar la sesión', 'Could not check your session')}
        </div>
        <div className="mt-1">{sesion.error.message}</div>
      </div>
    );
  }
  const destino = `${ubicacion.pathname}${ubicacion.search}`;
  if (!sesion.data || sesion.data.dosPasosPendiente) {
    return <Navigate to="/ingresar" replace state={{desde: destino}} />;
  }
  const {usuario} = sesion.data;
  if (usuario.debeCambiarContrasena && ubicacion.pathname !== '/cuenta/contrasena') {
    return <Navigate to="/cuenta/contrasena" replace />;
  }
  if (!usuario.debeCambiarContrasena && usuario.debeConfigurarDosPasos && ubicacion.pathname !== '/cuenta/dos-pasos') {
    return <Navigate to="/cuenta/dos-pasos" replace />;
  }
  return (
    <ContextoDeSesion.Provider value={sesion.data}>
      <Outlet />
    </ContextoDeSesion.Provider>
  );
}
